import {
    DateTimeTimeZone,
    DriveItem,
    Event,
    FileAttachment,
    Message,
    ResponseType,
} from "@microsoft/microsoft-graph-types";
import { AuthContextValue, EventBus } from "../../../contexts";
import {
    IDateValue,
    inheritHttpStatus,
    isNotFoundError,
    logger,
    LOGS,
    makeHttpError,
} from "../../../utils";
import {
    convertBase64URLToBinary,
    decodeBase64URL,
    encodeBase64URL,
} from "../../../utils/base64/base64";
import { buildGoogleQueryFromFilterObject } from "../../../utils/googleSearchQuery/google-search-query";
import { withThrottleRetry } from "../../../utils/throttle";
import { AgendaFilter } from "../../agenda";
import {
    CreateAgendaItemData,
    UpdateAgendaItemData,
} from "../../agenda/types/agenda";
import { ViewFilter } from "../../customView";
import {
    AttendeeResponseStatus,
    BodyContentType,
    EmailSearchFilter,
    EventMessageType,
    LinkedEventDetails,
    Mail,
    MailAttachment,
    MailComponentSize,
    MailCopyRef,
    MailSend,
    MailSender,
    NavItem,
    ReplyForward,
} from "../../mail";
import {
    GraphUserProfile,
    MailFlagStatus,
    MailFolder,
    MailImportance,
} from "../graph/types/graph";
import {
    AgendaEventsPage,
    AgendaItemBody,
    BatchDeleteItem,
    BatchDeleteResult,
    BatchReadStatusItem,
    BatchReadStatusResult,
    ContactPerson,
    DataSearchResult,
    IDataApi,
    SendMailResult,
    SentMailsCriteria,
} from "../IDataApi";
import {
    SENT_OLDEST_FIRST_MAX_PAGES,
    sentMailsQuery,
} from "./sent-search-logic";
import {
    Schema$Event,
    Schema$EventDateTime,
    Schema$Events,
} from "./types/calendar";
import {
    calendarApiUrl,
    driveApiUrl,
    gmailApiUrl,
    gmailBatchApiUrl,
    mapFolderToLabel,
    peopleApiUrl,
} from "./types/const";
import { Schema$File, Schema$FileList } from "./types/drive";
import {
    Schema$Label,
    Schema$ListMessagesResponse,
    Schema$Message,
    Schema$MessagePart,
    Schema$MessagePartHeader,
    Schema$Thread,
} from "./types/gmail";
import { Schema$SearchResponse } from "./types/person";

/** Pages de 1000 événements lues au plus par `getUserAgenda` (F-1.11). */
const AGENDA_MAX_PAGES = 10;

export class GoogleManager implements IDataApi {
    // #region Properties
    protected eventBus: EventBus;

    protected authContext: AuthContextValue;

    private fetchingGoogleUserProfile: Promise<any> | null = null;

    private googleUserProfile: GraphUserProfile | null = null;

    private boundaryForBatchRequest: string = "";

    private pageTokenCache: string | null = null;
    // #endregion Properties

    constructor(eventBus: EventBus, authContext: AuthContextValue) {
        logger.info(LOGS.GoogleManager, "()");

        this.eventBus = eventBus;
        this.authContext = authContext;
    }

    // #region Initialisation and cleanup
    /**
     * @method cleanup
     * Cleans up all resources and stops all active processes for the GoogleManager
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     * @public
     */
    public async _cleanup(): Promise<void> {
        logger.info(
            LOGS.GoogleManager,
            "GoogleManager.cleanup() - Starting cleanup process",
        );

        // Phase 1: Stop all active processes and timers
        try {
            logger.info(
                LOGS.GoogleManager,
                "  Phase 1: Stopping active processes",
            );

            // Cancel pending Graph user profile fetch if in progress
            if (this.fetchingGoogleUserProfile) {
                logger.info(
                    LOGS.GoogleManager,
                    "    Cancelling pending graph user profile fetch",
                );
                // Note: Can't actually cancel the Promise, but we'll reset the reference
            }
        } catch (error) {
            logger.error(
                LOGS.GoogleManager,
                `GoogleManager.cleanup() - Error in Phase 1: ${error}`,
            );
        }

        // Phase 2: Clear internal state and caches
        try {
            logger.info(
                LOGS.GoogleManager,
                "  Phase 2: Clearing internal state",
            );

            // Reset promises to their initial state
            this.fetchingGoogleUserProfile = null;

            // Clear cached user profile
            this.googleUserProfile = null;

            // Clear boundary timestamp
            this.boundaryForBatchRequest = "";

            // Clear page token
            this.pageTokenCache = null;
        } catch (error) {
            logger.error(
                LOGS.GoogleManager,
                `GoogleManager.cleanup() - Error in Phase 2: ${error}`,
            );
        }

        // Phase 3: Nullify manager references
        try {
            logger.info(
                LOGS.GoogleManager,
                "  Phase 3: Nullifying manager references",
            );

            // Set authContext reference to undefined
            this.authContext = undefined as any;
        } catch (error) {
            logger.error(
                LOGS.GoogleManager,
                `GoogleManager.cleanup() - Error in Phase 3: ${error}`,
            );
        }

        logger.info(
            LOGS.GoogleManager,
            "GoogleManager.cleanup() - Cleanup completed successfully",
        );
    }
    // #endregion Initialisation and cleanup

    // #region Public Methods

    //#region Mails
    public async searchForNewMails(
        filter: EmailSearchFilter,
    ): Promise<{ id: string; internetMessageId: string }[]> {
        logger.info(
            LOGS.GoogleManager,
            `searchForNewMails(${JSON.stringify(filter)})`,
        );

        try {
            // Construction de l'url de query pour recherche en fonction des filtres
            const params = buildGoogleQueryFromFilterObject(filter);
            const query = `${gmailApiUrl}/users/me/messages?${params}`;

            const response = await this.callGoogleApi(query);
            logger.info(
                LOGS.GoogleManager,
                `searchForNewMails Google response`,
                response,
            );

            let result = [];
            if (response.messages && response.messages.length > 0) {
                // Note: Getting Internet Message IDs would require batch fetching messages
                result = response.messages.map((message: { id: string }) => ({
                    id: message.id,
                    internetMessageId: message.id, // For now we use message id not internet message id for Google
                }));
            }

            return result;
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager],
                `Error in searchForNewMails: ${error}`,
            );
            if (error.details) {
                logger.info(
                    [LOGS.GoogleManager],
                    "searchForNewMailsError",
                    error.details,
                );
            }
            throw error;
        }
    }

    public async getMailById(mailId: string): Promise<Mail> {
        logger.info([LOGS.GoogleManager], `Getting mail by ID...`);

        const query = `${gmailApiUrl}/users/me/messages/${mailId}`;
        const response = await this.callGoogleApi(query);

        if (response.error) {
            logger.error(
                LOGS.GoogleManager,
                `Error during getMailById: ${response.error.message}`,
            );
            throw new Error(
                `    !!! Error during getMailById: ${response.error.message}`,
            );
        }

        const mailConverted: Mail = this._convertGmailToMail(response);

        return mailConverted;
    }

    // À REFONDRE (Google hors scope identité-mail) : fallback non-batché qui boucle sur getMailById
    // pour satisfaire le contrat IDataApi. Isolation par item (un échec n'arrête pas les autres).
    // Un vrai batching Gmail (batch endpoint) reste à implémenter le moment venu.
    public async getMailsByIds(
        mailIds: string[],
    ): Promise<{ mails: Mail[]; failedIds: string[] }> {
        logger.info(
            [LOGS.GoogleManager],
            `getMailsByIds(${mailIds.length}) — fallback boucle (Google)`,
        );
        const mails: Mail[] = [];
        const failedIds: string[] = [];
        for (const mailId of mailIds) {
            try {
                mails.push(await this.getMailById(mailId));
            } catch (error) {
                logger.warn(
                    [LOGS.GoogleManager],
                    `getMailsByIds (Google): échec ${mailId}, slot isolé : ${error}`,
                );
                failedIds.push(mailId);
            }
        }
        return { mails, failedIds };
    }

    public async getMailAttachments(
        mailId: string,
        _parentFolderId?: string,
    ): Promise<FileAttachment[]> {
        logger.info(LOGS.GoogleManager, `getMailAttachments(${mailId})`);

        try {
            // Construction de requête
            const request = `${gmailApiUrl}/users/me/messages/${mailId}?fields=id,threadId,internalDate,payload(parts(mimeType,filename,body))`;
            const response = await this.callGoogleApi(request);

            if (response.error) {
                logger.error(
                    LOGS.GoogleManager,
                    "Error during Mail Attachements fetching",
                );
                throw new Error("Error during Mail Attachements fetching");
            }

            logger.info(
                [LOGS.GoogleManager],
                `Attachments Results from Google API : ${JSON.stringify(response)}`,
            );

            // Filtrer les parts qui ont un filename non vide
            const attachments: FileAttachment[] = [];
            if (response.payload?.parts) {
                this._extractAttachmentsFromParts(response, attachments);
            }

            logger.info([LOGS.GoogleManager], "Attachment", attachments);

            return attachments;
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager],
                `Error fetching attachments for mail ${mailId}: ${error}`,
            );
            throw error; // Relance l'erreur pour qu'elle soit gérée par l'appelant
        }
    }

    public async deleteMail(mailId: string): Promise<void> {
        logger.info(LOGS.GoogleManager, `deleteMail(${mailId})...`);

        try {
            await this.callGoogleApi(
                `${gmailApiUrl}/users/me/messages/${mailId}`,
                "DELETE",
            );
        } catch (error) {
            logger.error([LOGS.GoogleManager], `Error in deleteMail: ${error}`);
            // AB#1764 — préserver le status HTTP (404 → suppression idempotente en amont).
            throw inheritHttpStatus(
                new Error(`Error deleting mail ${mailId}: ${error}`),
                error,
            ); // Relance l'erreur pour qu'elle soit gérée par l'appelant
        }
    }

    // ⚠️ À REFONDRE (modèle multi-copies, hors scope WS2) : Gmail n'expose ni un vrai
    //    internetMessageId (il faudrait fetcher les headers), ni un parentFolderId au sens Graph.
    //    Ci-dessous = compat de type uniquement (build vert), PAS une implémentation correcte :
    //    `id`/`internetMessageId` = id Gmail (lossy), `parentFolderId` = dossier interrogé.
    public async fetchGeneralInternetMessageIds(
        mailFolder: MailFolder = MailFolder.Inbox,
        top: number = 30,
        skip: number = 0,
    ): Promise<MailCopyRef[]> {
        logger.info(
            LOGS.GoogleManager,
            `fetchGeneralInternetMessageIds(${mailFolder}, ${top}, ${skip})`,
        );

        try {
            const getLabelFromFolder = mapFolderToLabel[mailFolder];
            const request = `${gmailApiUrl}/users/me/messages?maxResults=${top}&q=in:${getLabelFromFolder}&fields=messages(id),nextPageToken`;
            const response: Schema$ListMessagesResponse =
                await this.callGoogleApi(request);

            if (!response || !response.messages) {
                throw new Error(
                    "fetchGeneralInternetMessageIds - No data returned from Google API",
                );
            }

            // ⚠️ À REFONDRE : id Gmail utilisé comme id ET internetMessageId (lossy), parentFolderId = dossier interrogé.
            const generalCopyRefs: MailCopyRef[] = response.messages.map(
                (message: Schema$Message) => ({
                    id: message.id!,
                    internetMessageId: message.id!,
                    parentFolderId: mailFolder,
                }),
            );
            if (response.nextPageToken) {
                logger.warn(
                    [LOGS.GoogleManager, LOGS.AnalysisLifecycle],
                    `More results available at: ${request}&pageToken=${response.nextPageToken}`,
                );
            }

            logger.info(
                LOGS.GoogleManager,
                "fetchGeneralInternetMessageIds result",
                generalCopyRefs,
            );

            return generalCopyRefs;
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager, LOGS.AnalysisLifecycle],
                `Error in fetchGeneralInternetMessageIds: ${error}`,
            );
            throw `Error in fetchGeneralInternetMessageIds: ${error}`;
        }

        // TODO - à revoir
        // try {
        //     const getLabelFromFolder = mapFolderToLabel[mailFolder];
        //     const cacheKey = getLabelFromFolder;

        //     // Vérifier si on sait déjà qu'on a atteint la fin
        //     if (this.pageTokenCache[cacheKey] === null) {
        //         return [];
        //     }

        //     // Utiliser le token en cache si disponible
        //     const cachedToken = this.pageTokenCache[cacheKey];

        //     const baseRequest  = `${gmailApiUrl}/users/me/messages?maxResults=${top}&q=in:${getLabelFromFolder}&fields=messages(id),nextPageToken`
        //     const request      = cachedToken ? `${baseRequest}&pageToken=${cachedToken}` : baseRequest;
        //     const response     = await this.callGoogleApi(request);

        //     if (!response?.messages?.length) {
        //         this.pageTokenCache[cacheKey] = null;
        //         return [];
        //     }

        //     // Cacher le token pour la position suivante (null si fin atteinte)
        //     if (response.nextPageToken) {
        //         this.pageTokenCache[cacheKey] = response.nextPageToken;
        //     } else {
        //         // Pas de nextPageToken = fin de pagination
        //         this.pageTokenCache[cacheKey] = null;
        //     }

        //     // Gmail returns message IDs
        //     // NOTE - we need to fetch headers to get Internet Message ID
        //     generalInternetMessageIds = response.messages.map((message: Schema$Message) => message.id);

        //     // Note: Getting Internet Message IDs would require batch fetching messages
        //     // For now, returning Gmail message IDs
        //     return generalInternetMessageIds;

        // } catch (error) {
        //     return error;
        // }
    }

    /**
     * Les mails envoyés qui répondent aux critères, `limit` au plus (boîte « envoyes » de l'assistant).
     * Gmail rend les plus récents d'abord : « les plus anciens d'abord » lit jusqu'à
     * `SENT_OLDEST_FIRST_MAX_PAGES` pages, puis garde les derniers. Une lecture impossible lève.
     */
    public async searchSentMailCopyRefs(
        criteria: SentMailsCriteria,
        limit: number,
    ): Promise<MailCopyRef[]> {
        logger.info(LOGS.GoogleManager, `searchSentMailCopyRefs ...`);
        try {
            const oldestFirst = criteria.sortOrder === "ASC";
            const pageSize = oldestFirst ? 100 : Math.min(limit, 100);
            // La requête est encodée : « R&D » ou « jean+news@x.fr » ne coupent ni ne changent la recherche.
            const baseRequest = `${gmailApiUrl}/users/me/messages?q=${encodeURIComponent(sentMailsQuery(criteria))}&fields=messages.id,nextPageToken&maxResults=${pageSize}`;
            const ids: string[] = [];
            let nextPageToken: string | undefined = undefined;
            for (
                let page = 0;
                page < SENT_OLDEST_FIRST_MAX_PAGES &&
                (oldestFirst || ids.length < limit);
                page++
            ) {
                const response: Schema$ListMessagesResponse =
                    await this.callGoogleApi(
                        nextPageToken
                            ? `${baseRequest}&pageToken=${encodeURIComponent(nextPageToken)}`
                            : baseRequest,
                    );
                if (!response) {
                    throw new Error(
                        "searchSentMailCopyRefs - No data returned from Google API",
                    );
                }
                ids.push(
                    ...(response.messages ?? []).map(
                        (message: Schema$Message) => message.id!,
                    ),
                );
                nextPageToken = response.nextPageToken ?? undefined;
                if (!nextPageToken) break;
            }
            const kept = oldestFirst
                ? ids.reverse().slice(0, limit)
                : ids.slice(0, limit);
            // ⚠️ Comme fetchGeneralInternetMessageIds : id Gmail utilisé comme id ET internetMessageId.
            return kept.map((id) => ({
                id,
                internetMessageId: id,
                parentFolderId: MailFolder.SentItems,
            }));
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager],
                `Error in searchSentMailCopyRefs: ${error}`,
            );
            throw error;
        }
    }

    public async sendMail(mailData: MailSend): Promise<SendMailResult> {
        logger.info(
            LOGS.GoogleManager,
            `sendMail to ${mailData.toRecipients.join(", ")}...`,
        );

        try {
            // Create RFC 2822 formatted message
            const email = this._createRFC2822Message(mailData);

            const response = await this.callGoogleApi(
                `${gmailApiUrl}/users/me/messages/send`,
                "POST",
                { raw: email },
            );

            return { mailSentId: response.id };
        } catch (error) {
            logger.error([LOGS.GoogleManager], `Error in sendMail: ${error}`);
            throw new Error(`  !!! Error in sendMail: ${error}`);
        }
    }

    public async replyToMail(
        messageId: string,
        replyData: ReplyForward,
        replyAll: boolean = false,
    ): Promise<SendMailResult> {
        const replyType = replyAll ? "replyAll" : "reply";
        logger.info(
            [LOGS.GoogleManager],
            `${replyType} to message ${messageId}...`,
        );

        try {
            // Récupérer le message original
            const originalMessage: Schema$Message = await this.callGoogleApi(
                `${gmailApiUrl}/users/me/messages/${messageId}`,
            );

            if (!originalMessage) {
                throw new Error(`Message ${messageId} not found`);
            }

            // Contruction du corps du mail
            const { mailSendData, inReplyTo, references } =
                this._buildMailResponse(
                    originalMessage,
                    replyData,
                    "reply",
                    replyAll,
                );

            // Créer le message
            const email = this._createRFC2822Message(
                mailSendData,
                inReplyTo,
                references,
                originalMessage.threadId!,
            );

            // Envoyer de la réponse
            const response = await this.callGoogleApi(
                `${gmailApiUrl}/users/me/messages/send`,
                "POST",
                { raw: email },
            );

            return { mailSentId: response.id };
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager],
                `Error in replyToMail: ${error}`,
            );
            throw new Error(`Error in replyToMail: ${error}`);
        }
    }

    public async resolveSentMailId(
        _internetMessageId: string,
    ): Promise<string | null> {
        return null;
    }

    public async setReadStatus(
        messageId: string,
        isRead: boolean,
    ): Promise<boolean> {
        logger.info(
            LOGS.GoogleManager,
            `Setting read status to ${isRead} for message ${messageId}...`,
        );

        try {
            const body = isRead
                ? { removeLabelIds: ["UNREAD"] }
                : { addLabelIds: ["UNREAD"] };

            await this.callGoogleApi(
                `${gmailApiUrl}/users/me/messages/${messageId}/modify`,
                "POST",
                body,
            );
            return true;
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager],
                `Error in setReadStatus: ${error}`,
            );
            return false;
        }
    }

    /**
     * Marque un lot d'emails comme lus/non-lus (fallback séquentiel Google)
     * ════════════════════════════════════════════════════════════════════════
     *
     * Google Gmail API ne supportant pas le JSON Batching de la même façon que
     * Microsoft Graph, les appels sont effectués séquentiellement via `setReadStatus`.
     * Pas de retry intégré — chaque échec est classé directement dans `failed`.
     *
     * @param items  - Liste des items { messageId, isRead }
     * @returns      - `success` : IDs mis à jour avec succès / `failed` : IDs en échec
     */
    public async batchSetReadStatus(
        items: BatchReadStatusItem[],
    ): Promise<BatchReadStatusResult> {
        const result: BatchReadStatusResult = { success: [], failed: [] };
        for (const item of items) {
            const ok = await this.setReadStatus(item.messageId, item.isRead);
            (ok ? result.success : result.failed).push(item.messageId);
        }
        return result;
    }

    public async setImportance(
        messageId: string,
        importance: MailImportance,
    ): Promise<boolean> {
        logger.info(
            LOGS.GoogleManager,
            `Setting importance to ${messageId}, ${importance}`,
        );

        try {
            const body =
                importance === MailImportance.Low ||
                importance === MailImportance.Normal
                    ? { removeLabelIds: ["IMPORTANT"] }
                    : { addLabelIds: ["IMPORTANT"] };

            await this.callGoogleApi(
                `${gmailApiUrl}/users/me/messages/${messageId}/modify`,
                "POST",
                body,
            );
            return true;
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager],
                `Error in setImportance: ${error}`,
            );
            return false;
        }
    }

    public async setFlag(
        messageId: string,
        flag: { flagStatus: MailFlagStatus } | null,
    ): Promise<boolean> {
        logger.info(
            LOGS.GoogleManager,
            `Setting flag for message${messageId}, ${flag}`,
        );

        try {
            const body =
                flag?.flagStatus === MailFlagStatus.Flagged
                    ? { addLabelIds: ["STARRED"] }
                    : { removeLabelIds: ["STARRED"] };

            await this.callGoogleApi(
                `${gmailApiUrl}/users/me/messages/${messageId}/modify`,
                "POST",
                body,
            );
            return true;
        } catch (error) {
            logger.error([LOGS.GoogleManager], `Error in setFlag: ${error}`);
            return false;
        }
    }

    public async searchContact(
        searchString: string,
        top: number = 20,
    ): Promise<string[]> {
        const people = await this.searchContactPeople(searchString, top);
        return people.map((person) => person.email);
    }

    /**
     * Les contacts de l'utilisateur (Google People) qui répondent à la recherche, chacun avec son nom
     * affiché ; une adresse une seule fois. `searchContact` en rend les adresses seules.
     */
    public async searchContactPeople(
        searchString: string,
        top: number = 20,
    ): Promise<ContactPerson[]> {
        logger.info(
            [LOGS.GoogleManager],
            `searchContact for ${searchString}...`,
        );
        logger.warn(
            LOGS.GoogleManager,
            `Google recherche uniquement dans les contacts de l'utilisateur`,
        );

        try {
            const query = `query=${searchString}&readMask=names,emailAddresses&pageSize=${top}&fields=results(person(resourceName,names(displayName),emailAddresses(value)))`;
            const response: Schema$SearchResponse = await this.callGoogleApi(
                `${peopleApiUrl}/people:searchContacts?${query}`,
            );

            logger.info(
                [LOGS.GoogleManager],
                "Contacts: ",
                response.results || [],
            );

            const unique = new Map<string, ContactPerson>();
            for (const result of response.results ?? []) {
                const email = result.person?.emailAddresses?.[0]?.value;
                if (email != null && !unique.has(email)) {
                    unique.set(email, {
                        email,
                        displayName:
                            result.person?.names?.[0]?.displayName ?? "",
                    });
                }
            }
            const uniquePeople = [...unique.values()];

            logger.info(
                [LOGS.GoogleManager],
                "emails",
                uniquePeople.map((person) => person.email),
            );

            return uniquePeople;
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager],
                `Error in searchContact: ${error}`,
            );
            if (error.details) {
                logger.info(
                    [LOGS.GoogleManager],
                    "SearchContactError",
                    error.details,
                );
            }
            throw error;
        }
    }

    /**
     * `limit` (assistant, F-1.2) : pages de `min(limit, 100)`, arrêt dès la limite atteinte, coupe à
     * `limit`, et une erreur de page remonte. Sans `limit` (vues, « voir plus ») : pages de 100 et une
     * erreur de page arrête la lecture, comme avant.
     */
    public async searchInternetMessageIdsFromViewFilter(
        viewFilter: ViewFilter,
        limit?: number,
    ): Promise<DataSearchResult[]> {
        logger.info(
            LOGS.GoogleManager,
            `searchMailsFromUpdatedViewFilter with filter ${JSON.stringify(viewFilter)}, ${limit}`,
        );

        let internetMessageIds: string[] = [];

        try {
            // Construction de la query Gmail à partir du ViewFilter
            let queryParts: string[] = [];

            // 1. Filtrage par mots-clés dans le sujet
            if (viewFilter.keywords && viewFilter.keywords.length > 0) {
                const keywordConditions = viewFilter.keywords.map(
                    (keyword) => `subject:"${keyword.replace(/"/g, '\\"')}"`,
                );
                queryParts.push(`(${keywordConditions.join(" OR ")})`);
            }

            // 2. Filtrage par date de début
            if (viewFilter.startDate) {
                const startDate = new Date(viewFilter.startDate);
                if (!isNaN(startDate.getTime())) {
                    const startTimestamp = Math.floor(
                        startDate.getTime() / 1000,
                    );
                    queryParts.push(`after:${startTimestamp}`);
                }
            }

            // 3. Filtrage par date de fin
            if (viewFilter.endDate) {
                const endDate = new Date(viewFilter.endDate);
                if (!isNaN(endDate.getTime())) {
                    const endTimestamp = Math.floor(endDate.getTime() / 1000);
                    queryParts.push(`before:${endTimestamp}`);
                }
            }

            // 4. Filtrage par expéditeurs identifiés
            if (
                viewFilter.identifiedSenders &&
                viewFilter.identifiedSenders.length > 0
            ) {
                const senderConditions = viewFilter.identifiedSenders.map(
                    (sender) => `from:"${sender.replace(/"/g, '\\"')}"`,
                );
                queryParts.push(`(${senderConditions.join(" OR ")})`);
            }

            // 5. Filtrage par destinataires identifiés
            if (
                viewFilter.identifiedRecipients &&
                viewFilter.identifiedRecipients.length > 0
            ) {
                const recipientConditions = viewFilter.identifiedRecipients.map(
                    (recipient) => `to:"${recipient.replace(/"/g, '\\"')}"`,
                );
                queryParts.push(`(${recipientConditions.join(" OR ")})`);
            }

            // 6. Filtrage par statut de lecture
            if (viewFilter.readStatus) {
                if (viewFilter.readStatus === "read") {
                    queryParts.push("is:read");
                } else if (viewFilter.readStatus === "unread") {
                    queryParts.push("is:unread");
                }
            }

            // 7. Filtrage par pièces jointes
            if (viewFilter.hasAttachment !== undefined) {
                if (viewFilter.hasAttachment) {
                    queryParts.push("has:attachment");
                } else {
                    queryParts.push("-has:attachment");
                }
            }

            // Construction de la query complète
            const searchQuery = queryParts.join(" ");
            logger.info(
                [LOGS.GoogleManager],
                `Gmail search query: ${searchQuery}`,
            );

            // Exécution de la requête Gmail API
            // Ordre de tri (Gmail n'a pas d'orderby explicite, tri par date par défaut)
            // Note: Gmail trie toujours par date, l'ordre peut être inversé avec newest/oldest dans certains contextes
            const pageSize = limit ? Math.min(limit, 100) : 100;
            const baseRequest = `${gmailApiUrl}/users/me/messages?q=${searchQuery}&fields=messages.id,nextPageToken&maxResults=${pageSize}`;

            let nextPageToken: string | undefined = undefined;
            let pageCount = 0;
            const maxPages = 50;

            do {
                pageCount++;
                logger.info(
                    [LOGS.GoogleManager, LOGS.AnalysisLifecycle],
                    `Processing page ${pageCount}...`,
                );

                try {
                    // Construction de l'URL avec ou sans pageToken
                    const requestUrl = nextPageToken
                        ? `${baseRequest}&pageToken=${nextPageToken}`
                        : baseRequest;

                    const response = await this.callGoogleApi(requestUrl);

                    // Vérifier que la réponse contient des messages
                    if (
                        !response ||
                        !response.messages ||
                        response.messages.length === 0
                    ) {
                        logger.info(
                            [LOGS.GoogleManager],
                            `No more messages found on page ${pageCount}`,
                        );
                        break;
                    }

                    // Ajouter les IDs trouvés
                    const pageIds = response.messages.map(
                        (item: Schema$Message) => item.id,
                    );
                    internetMessageIds.push(...pageIds);

                    logger.info(
                        [LOGS.GoogleManager, LOGS.AnalysisLifecycle],
                        `Found ${pageIds.length} mails on page ${pageCount}`,
                    );

                    // Limite demandée atteinte : inutile de lire la page suivante
                    if (limit && internetMessageIds.length >= limit) {
                        break;
                    }

                    // Mise à jour du token pour la page suivante
                    // IMPORTANT: nextPageToken peut être undefined, null, ou une chaîne vide
                    nextPageToken = response.nextPageToken;

                    // Sécurité contre les boucles infinies
                    if (pageCount >= maxPages) {
                        logger.warn(
                            [LOGS.GoogleManager],
                            `Reached maximum page limit (${maxPages}) for searchMailsFromUpdatedViewFilter`,
                        );
                        break;
                    }
                } catch (pageError) {
                    logger.error(
                        [LOGS.GoogleManager],
                        `Error processing page ${pageCount}: ${pageError}`,
                    );
                    // Avec une limite (assistant), l'erreur remonte : une liste tronquée en silence
                    // passerait pour complète. Sans limite (vues), on garde les pages déjà lues.
                    if (limit) {
                        throw pageError;
                    }
                    break;
                }
            } while (nextPageToken && nextPageToken.trim() !== ""); // Condition de sortie claire

            if (limit) {
                internetMessageIds = internetMessageIds.slice(0, limit);
            }

            logger.info(
                [LOGS.GoogleManager],
                `searchMailsFromUpdatedViewFilter - found ${internetMessageIds.length} emails matching criteria`,
            );

            logger.warn(
                LOGS.GoogleManager,
                "GoogleManager returns DataSearchResults without content for KeyWords or Dests Post-Filter. DataManager should keep every mail without proper infos to filter on !!!",
            );
            // This may not be needed if google filters are properly implemented
            //   => Be carefull, DataManager post filter behavior must be adapted not to attempt filter on Google results
            //   => DataManager does not know if it's Google or Microsoft
            //   => DataManager skips post filter if to, cc, bcc, title, uniqueBody are empty, which should happen only on Google
            //   => need conception to do it clean !!!

            const dataSearchResult: DataSearchResult[] = internetMessageIds.map(
                (internetMessageId) => {
                    return {
                        internetMessageId: internetMessageId,
                    } as DataSearchResult;
                },
            );

            return dataSearchResult;
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager],
                `Error in searchInternetMessageIdsFromViewFilter: ${error}`,
            );
            if (error.details) {
                logger.info([LOGS.GoogleManager], "Error", error.details);
            }

            throw error;
        }
    }

    /**
     * Supprime un lot d'emails (fallback séquentiel Google)
     * ═══════════════════════════════════════════════════════
     *
     * Google Gmail API ne supportant pas le JSON Batching de la même façon que
     * Microsoft Graph, les opérations sont effectuées séquentiellement :
     * - `isPermanent = true`  → `deleteMail` (suppression définitive)
     * - `isPermanent = false` → `moveMail` vers `MailFolder.Deleteditems` (corbeille)
     *
     * Pas de retry intégré — chaque exception est capturée et classe l'item dans `failed`.
     *
     * @param items  - Liste des items { messageId, isPermanent }
     * @returns      - `permanentDeleted` / `moved` (avec newMessageId) / `failed`
     */
    public async batchDeleteMails(
        items: BatchDeleteItem[],
    ): Promise<BatchDeleteResult> {
        const result: BatchDeleteResult = {
            permanentDeleted: [],
            moved: [],
            notFound: [],
            failed: [],
        };
        for (const item of items) {
            try {
                if (item.isPermanent) {
                    await this.deleteMail(item.messageId);
                    result.permanentDeleted.push(item.messageId);
                } else {
                    const moved = await this.moveMail(
                        item.messageId,
                        MailFolder.Deleteditems,
                    );
                    result.moved.push({
                        messageId: item.messageId,
                        newMessageId: moved?.id ?? item.messageId,
                    });
                }
            } catch (error) {
                // AB#1764 — mail déjà supprimé côté messagerie (404) → suppression idempotente.
                if (isNotFoundError(error)) {
                    result.notFound.push(item.messageId);
                } else {
                    result.failed.push(item.messageId);
                }
            }
        }
        return result;
    }

    public async moveMail(
        mailId: string,
        destinationId: string,
    ): Promise<any | Schema$Message> {
        logger.info(
            [LOGS.GoogleManager],
            `Moving mail ${mailId} to folder ${destinationId}...`,
        );

        try {
            const labelDestination = mapFolderToLabel[destinationId];

            if (!labelDestination) {
                logger.error(
                    [LOGS.GoogleManager],
                    `Error in moveMail: folder ${destinationId} doesn't exist in mapping`,
                );
                throw new Error(
                    ` !!! Error in moveMail: folder ${destinationId} doesn't exist in mapping`,
                );
            }

            // Gmail uses labels, not folders - modify labels instead
            const movedMessage: Schema$Message = await this.callGoogleApi(
                `${gmailApiUrl}/users/me/messages/${mailId}/modify`,
                "POST",
                { addLabelIds: [labelDestination] },
            );
            return movedMessage;
        } catch (error) {
            logger.error([LOGS.GoogleManager], `Error in moveMail: ${error}`);
            // AB#1764 — préserver le status HTTP (404 → suppression idempotente en amont).
            throw inheritHttpStatus(
                new Error(`Error moving mail: ${error}`),
                error,
            );
        }
    }

    public async getMailThreadInternetMessageIds(
        conversationId: string,
    ): Promise<Message[]> {
        logger.info(
            LOGS.GoogleManager,
            `getMailThreadInternetMessageIds(${conversationId})`,
        );

        try {
            const getMessagesIds: Schema$Thread = await this.callGoogleApi(
                `${gmailApiUrl}/users/me/threads/${conversationId}?fields=messages(id)`,
            );

            if (!getMessagesIds.messages) {
                throw "Unexpected messages on thread requested";
            }

            logger.info(
                LOGS.GoogleManager,
                "MailThread content",
                getMessagesIds,
            );

            // Note: Getting Internet Message IDs would require batch fetching messages
            const result = getMessagesIds.messages.map(
                (message: Schema$Message) => ({
                    id: message.id,
                    internetMessageId: message.id, // For now we use message id not internet message id for Google
                }),
            );

            return result as Message[];
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager],
                `Error in getMailThreadInternetMessageIds: ${error}`,
            );
            if (error.details) {
                logger.info(
                    [LOGS.GoogleManager],
                    "getMailThreadInternetMessageIdsError",
                    error.details,
                );
            }
            throw error;
        }
    }

    public async getGeneralNavBarContent(
        mailFolders: string[],
    ): Promise<NavItem[]> {
        logger.info(
            [LOGS.GoogleManager],
            `getGeneralNavBarContent(${mailFolders.join(", ")})`,
        );

        try {
            // Construire les requêtes batch pour les labels spécifiques
            this.boundaryForBatchRequest = `batch_${Date.now()}`;

            // Construire le corps de la requête batch
            let batchBody = "";
            mailFolders.forEach((folder, index) => {
                const labelId = mapFolderToLabel[folder];

                if (labelId) {
                    // Gère les labels présent dans le mapping
                    batchBody += `--${this.boundaryForBatchRequest}\r\n`;
                    batchBody += `Content-Type: application/http\r\n`;
                    batchBody += `Content-ID: ${index + 1}\r\n\r\n`;
                    batchBody += `GET /gmail/v1/users/me/labels/${labelId} HTTP/1.1\r\n`;
                    batchBody += `Host: gmail.googleapis.com\r\n\r\n`;
                }
            });
            batchBody += `--${this.boundaryForBatchRequest}--\r\n`;

            // Exécuter la requête batch. `callGoogleApi` gère déjà le retry 429
            // (Retry-After + backoff borné via withThrottleRetry) ; l'ancien wrapper
            // exponentialBackoff (détection par string 'TooManyRequests') est supprimé.
            const response = await this.callGoogleApi(
                gmailBatchApiUrl,
                "POST",
                batchBody,
                true,
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Parser la réponse batch (adaptation de _parseBatchResponse pour les labels)
            const responseText = await response.text();
            const labels: Schema$Label[] =
                this._parseBatchLabelsResponse(responseText);

            // Convertir les labels en NavItems
            const navItems: NavItem[] = [];

            for (const folder of mailFolders) {
                const labelName = mapFolderToLabel[folder];

                if (labelName) {
                    // Gère les labels présent dans le mapping
                    const labelData = labels.find(
                        (label) => label.id === labelName,
                    );

                    if (labelData) {
                        const navItem: NavItem = {
                            id: folder,
                            menuInfo: {
                                title: labelData.name || folder, // A revoir
                                icon: "",
                                color: "#64748B",
                            },
                            itemCount: labelData.messagesUnread || 0,
                            isFavorite: false,
                        };

                        if (folder === MailFolder.Inbox) {
                            this.eventBus.emit(
                                "inbox-folder-id-updated",
                                folder,
                            );
                        } else {
                            this.eventBus.emit("mail-folder-id-updated", {
                                folderName: folder,
                                folderId: folder,
                            });
                        }

                        navItems.push(navItem);
                    }
                }
            }

            logger.info(LOGS.GoogleManager, `navItems :`, navItems);

            return navItems;
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager],
                `Error in getGeneralNavBarContent: ${error}`,
            );
            return [];
        }
    }

    public async getMailsParentFolderIds(
        internetMessageIds: string[],
    ): Promise<Record<string, string>> {
        logger.warn(
            [LOGS.GoogleManager],
            "getMailsParentFolderIds - Not implemented yet !!!",
        );

        return {};
    }
    //#endregion

    //#region Agenda

    /**
     * Agenda complet d'une fenêtre (écran d'agenda, créneaux libres de l'assistant — F-1.11) :
     *  - `singleEvents=true` : chaque occurrence d'une série arrive à sa date (sans lui, Google rend
     *    la série elle-même, à la date de sa première occurrence) ; `orderBy=startTime` l'exige ;
     *  - toutes les pages, `AGENDA_MAX_PAGES` au plus (au-delà, un log) ;
     *  - `status`, `transparency` et `attendees` disent si l'événement occupe vraiment son créneau
     *    (annulé, marqué disponible, refusé — F-1.12).
     * Une erreur remonte : rendue en liste vide, elle faisait dire « tout est libre ».
     */
    public async getUserAgenda(filter: AgendaFilter): Promise<Event[]> {
        logger.info(LOGS.GoogleManager, `getUserAgenda`);

        const startDateISO = new Date(filter.startDate!).toISOString();
        const endDateISO = new Date(filter.endDate!).toISOString();

        try {
            const selectedFields =
                "fields=items(id,summary,description,start,end,organizer,attendees,location,conferenceData,status,transparency),nextPageToken";
            const baseUrl =
                `${calendarApiUrl}/calendars/primary/events?` +
                `timeMin=${startDateISO}&` +
                `timeMax=${endDateISO}&` +
                `singleEvents=true&orderBy=startTime&` +
                `maxResults=1000&` +
                selectedFields;

            const googleEvents: Schema$Event[] = [];
            let pageToken: string | undefined;
            let pageCount = 0;
            do {
                const googleEventsList: Schema$Events =
                    await this.callGoogleApi(
                        pageToken
                            ? `${baseUrl}&pageToken=${encodeURIComponent(pageToken)}`
                            : baseUrl,
                    );
                pageCount++;

                // Fenêtre sans événement : Google omet `items` (réponse partielle), liste vide.
                googleEvents.push(...(googleEventsList.items ?? []));
                pageToken = googleEventsList.nextPageToken || undefined;
            } while (pageToken && pageCount < AGENDA_MAX_PAGES);

            if (pageToken) {
                logger.warn(
                    LOGS.GoogleManager,
                    `getUserAgenda - stopped after ${AGENDA_MAX_PAGES} pages (${googleEvents.length} events), the rest of the window is not read`,
                );
            }

            // Mapper Google Events vers Graph Events
            const mapGoogleEventToGraphEvent: Event[] = googleEvents.map(
                (googleEvent: Schema$Event) =>
                    this._mapGoogleEventToGraphEvent(googleEvent),
            );

            return mapGoogleEventToGraphEvent;
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager],
                `Error in getUserAgenda: ${error}`,
            );
            // L'erreur remonte telle quelle (429 typé compris) : l'AgendaManager rejoue une fois
            // les autres erreurs, puis l'écran ou l'assistant affiche une erreur.
            throw error;
        }
    }

    /**
     * Paginated calendar retrieval (AB#158). Honors filter.startDate/endDate as the window,
     * orders by startTime ascending (singleEvents expands recurrences), pages via `pageToken`.
     * The opaque cursor carries the full request URL + pageToken so the next page reuses the
     * same window/params. `includeBody` is ignored: Google exposes only `description`, mapped
     * into `bodyPreview` — there is no separate rich `body`.
     *
     * Contrat d'erreur (#3, corrigé par F-1.11) : l'erreur remonte telle quelle, 429 typé compris,
     * comme `GraphManager.getUserAgendaPage` : le nouvel essai NON-429 de
     * `AgendaManager.getUserAgendaPage` s'applique, et un échec ne passe plus pour une fenêtre
     * épuisée (« aucun évènement », ou « charger plus » qui s'arrête en silence).
     *
     * ⚠️ PARITÉ GOOGLE INCOMPLÈTE (AB#158) — un écart connu avec Graph, à traiter lors de
     *    l'implémentation Google (le TODO ci-dessous marque le lieu et la recette) :
     *
     *    • #4 Tri : `orderBy` est CODÉ EN DUR `startTime` ascendant → `filter.orderBy` (honoré par
     *      Graph via `$orderby=start/dateTime ASC|DESC`) est ignoré. L'API Google Calendar ne
     *      supporte QUE `startTime` (asc, exige `singleEvents=true`) et `updated` (asc) — PAS de
     *      descendant côté serveur. Aujourd'hui une demande DESC (« mes réunions les plus récentes »)
     *      montre à un utilisateur Google les 6 évènements les PLUS ANCIENS.
     *      → À l'implémentation : pour DESC, récupérer toute la fenêtre (bornée ≤ +1 an par l'api-ai)
     *        en ascendant puis inverser + paginer côté client (⚠️ perte de la pagination paresseuse
     *        côté serveur), OU documenter/refuser explicitement le DESC côté Google.
     */
    public async getUserAgendaPage(
        filter: AgendaFilter,
        cursor?: string,
        _options?: { includeBody?: boolean },
    ): Promise<AgendaEventsPage> {
        logger.info(
            LOGS.GoogleManager,
            `getUserAgendaPage(cursor: ${cursor ? "yes" : "no"})`,
        );

        try {
            let requestUrl: string;

            if (cursor) {
                // Google cursor = the full request URL carrying pageToken (built on the previous page).
                requestUrl = cursor;
            } else {
                if (!filter.startDate || !filter.endDate) {
                    throw new Error(
                        "startDate and endDate are required in AgendaFilter",
                    );
                }
                const startDateISO = new Date(filter.startDate).toISOString();
                const endDateISO = new Date(filter.endDate).toISOString();
                const pageSize = filter.limit || 25;
                const selectedFields =
                    "fields=items(id,summary,description,start,end,organizer,attendees,location,conferenceData),nextPageToken";
                // TODO (#4, parité Google) : honorer `filter.orderBy`. `startTime` est asc-only
                // côté Google (pas de DESC serveur) → pour un DESC, inverser + paginer côté client
                // sur la fenêtre complète (bornée ≤ +1 an). Cf. le docblock de la méthode.
                requestUrl =
                    `${calendarApiUrl}/calendars/primary/events?` +
                    `timeMin=${startDateISO}&` +
                    `timeMax=${endDateISO}&` +
                    `singleEvents=true&orderBy=startTime&` +
                    `maxResults=${pageSize}&` +
                    selectedFields;
            }

            const googleEventsList: Schema$Events =
                await this.callGoogleApi(requestUrl);

            const items = googleEventsList.items || [];
            const events: Event[] = items.map((googleEvent: Schema$Event) =>
                this._mapGoogleEventToGraphEvent(googleEvent),
            );

            let nextCursor: string | undefined;
            if (googleEventsList.nextPageToken) {
                const baseUrl = requestUrl.replace(/&pageToken=[^&]*/g, "");
                nextCursor = `${baseUrl}&pageToken=${encodeURIComponent(googleEventsList.nextPageToken)}`;
            }

            return { events, nextCursor };
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager],
                `Error in getUserAgendaPage: ${error}`,
            );
            // L'erreur remonte telle quelle, 429 typé compris (#3, F-1.11) : cf. le docblock.
            throw error;
        }
    }

    /**
     * Google Calendar exposes only `description`, already mapped into `bodyPreview` during the
     * scan (no separate rich `body`) → nothing to hydrate. Returns empty (no network call).
     */
    public async getAgendaItemBodies(
        _ids: string[],
    ): Promise<Record<string, AgendaItemBody>> {
        return {};
    }

    /**
     * Création d'un événement dans le calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {UpdateAgendaItemData} updateData - Données à modifier
     * @output {Promise<boolean>} Succès de la mise à jour
     */
    public async createAgendaItem(
        agendaItemData: CreateAgendaItemData,
    ): Promise<boolean> {
        logger.info(
            LOGS.GoogleManager,
            `createAgendaItem(${agendaItemData.subject})`,
        );

        try {
            // Construction du body minimal pour Google Calendar API
            const googleEvent: Schema$Event = {
                summary: agendaItemData.subject,
                start: {
                    dateTime: new Date(agendaItemData.start).toISOString(),
                    timeZone: "UTC",
                },
                end: {
                    dateTime: new Date(agendaItemData.end).toISOString(),
                    timeZone: "UTC",
                },
                description: agendaItemData.description || "",
            };

            // Événement « toute la journée » : Google attend { date } sans heure (cf. updateAgendaItem)
            if (agendaItemData.isAllDay) {
                googleEvent.start = {
                    date: new Date(agendaItemData.start)
                        .toISOString()
                        .split("T")[0],
                };
                googleEvent.end = {
                    date: new Date(agendaItemData.end)
                        .toISOString()
                        .split("T")[0],
                };
            }

            // Ajouter la localisation si fournie
            if (agendaItemData.location) {
                googleEvent.location = agendaItemData.location;
            }

            // Ajouter les participants si fournis
            if (agendaItemData.attendees?.length) {
                const userEmail = this.googleUserProfile?.mail;
                googleEvent.attendees = agendaItemData.attendees.map(
                    (email) => ({
                        email,
                        responseStatus:
                            email === userEmail ? "accepted" : "needsAction",
                    }),
                );
            }

            logger.info(LOGS.GoogleManager, "googleEvent object", googleEvent);

            // Appel API
            const response = await this.callGoogleApi(
                `${calendarApiUrl}/calendars/primary/events`,
                "POST",
                googleEvent,
            );

            logger.info(
                LOGS.GoogleManager,
                `Event created successfully with ID: ${response.id}`,
            );
            return true;
        } catch (error) {
            logger.error(
                LOGS.GoogleManager,
                `Error in createAgendaItem: ${error}`,
            );
            return false;
        }
    }

    /**
     * Met à jour un événement existant dans le calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à mettre à jour
     * @input {UpdateAgendaItemData} updateData - Données à modifier
     * @output {Promise<boolean>} Succès de la mise à jour
     */
    public async updateAgendaItem(
        itemId: string,
        updateData: UpdateAgendaItemData,
    ): Promise<boolean> {
        logger.info(LOGS.GoogleManager, `updateAgendaItem(${itemId})`);

        try {
            // Construction de l'objet de mise à jour Google
            const googleEventUpdated: Schema$Event = {};

            // Champs simples
            if (updateData.subject !== undefined) {
                googleEventUpdated.summary = updateData.subject;
            }

            if (updateData.description !== undefined) {
                googleEventUpdated.description = updateData.description;
            }

            if (updateData.location !== undefined) {
                googleEventUpdated.location = updateData.location;
            }

            // Mise à jour temporelle
            if (updateData.timeUpdate) {
                const timeZone = "UTC";

                googleEventUpdated.start = {
                    dateTime: updateData.timeUpdate.start,
                    timeZone: timeZone,
                };

                googleEventUpdated.end = {
                    dateTime: updateData.timeUpdate.end,
                    timeZone: timeZone,
                };

                // Événements toute la journée
                if (updateData.timeUpdate.isAllDay) {
                    googleEventUpdated.start = {
                        date: updateData.timeUpdate.start.split("T")[0],
                    };
                    googleEventUpdated.end = {
                        date: updateData.timeUpdate.end.split("T")[0],
                    };
                }
            }

            // Participants
            if (updateData.attendees !== undefined) {
                const userEmail = this.googleUserProfile?.mail;
                googleEventUpdated.attendees = updateData.attendees.map(
                    (email) => ({
                        email: email,
                        responseStatus:
                            email === userEmail ? "accepted" : "needsAction",
                    }),
                );
            }

            // Métadonnées - Mapping vers Google
            if (updateData.importance !== undefined) {
                // Google n'a pas d'importance directe, on utilise colorId
                googleEventUpdated.colorId =
                    updateData.importance === "high" ? "11" : "1";
            }

            // Categories pas gérées côté Google
            // if (updateData.categories !== undefined) {
            //     // Mapper la première catégorie vers colorId
            //     updatePayload.colorId = this.mapCategoryToColorId(updateData.categories[0] || '');
            // }

            if (updateData.sensitivity !== undefined) {
                googleEventUpdated.visibility =
                    updateData.sensitivity === "normal" ? "default" : "private";
            }

            if (updateData.showAs !== undefined) {
                googleEventUpdated.transparency =
                    updateData.showAs === "free" ? "transparent" : "opaque";
            }

            if (
                updateData.isReminderOn !== undefined ||
                updateData.reminderMinutesBeforeStart !== undefined
            ) {
                if (
                    updateData.isReminderOn &&
                    updateData.reminderMinutesBeforeStart
                ) {
                    googleEventUpdated.reminders = {
                        useDefault: false,
                        overrides: [
                            {
                                method: "popup",
                                minutes: updateData.reminderMinutesBeforeStart,
                            },
                        ],
                    };
                } else if (updateData.isReminderOn === false) {
                    googleEventUpdated.reminders = {
                        useDefault: false,
                        overrides: [],
                    };
                }
            }

            // Vérifier qu'au moins un champ est à mettre à jour
            if (Object.keys(googleEventUpdated).length === 0) {
                logger.warn(
                    LOGS.GoogleManager,
                    "No fields to update in agenda item",
                );
                return true;
            }

            logger.info(
                LOGS.GoogleManager,
                "Update payload",
                googleEventUpdated,
            );

            // Appel PATCH à l'API Google Calendar
            await this.callGoogleApi(
                `${calendarApiUrl}/calendars/primary/events/${itemId}`,
                "PATCH",
                googleEventUpdated,
            );

            logger.info(LOGS.GoogleManager, "Agenda Item updated successfully");
            return true;
        } catch (error) {
            logger.error(
                LOGS.GoogleManager,
                `Error in updateAgendaItem: ${error}`,
            );
            return false;
        }
    }

    /**
     * Supprime un événement du calendrier de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {string} itemId - ID de l'événement à supprimer
     * @output {Promise<boolean>} Succès de la suppression
     */
    public async deleteAgendaItem(itemId: string): Promise<boolean> {
        logger.info(LOGS.GoogleManager, `deleteAgendaItem(${itemId})`);

        try {
            await this.callGoogleApi(
                `${calendarApiUrl}/calendars/primary/events/${itemId}`,
                "DELETE",
            );

            logger.info(LOGS.GoogleManager, "Agenda Item deleted successfully");
            return true;
        } catch (error) {
            logger.error([LOGS.GoogleManager], `Error in deleteMail: ${error}`);
            return false;
        }
    }

    public async acceptAgendaItem(itemId: string): Promise<boolean> {
        logger.info(LOGS.GoogleManager, `acceptAgendaItem(${itemId})`);

        try {
            // 1. Récupérer l'événement existant
            const eventResponse: Schema$Event = await this.callGoogleApi(
                `${calendarApiUrl}/calendars/primary/events/${itemId}`,
            );

            if (
                !eventResponse.attendees ||
                !Array.isArray(eventResponse.attendees)
            ) {
                logger.error(LOGS.GoogleManager, "No attendees found in event");
                throw "Unexpected attendees found in event";
            }

            // 2. Trouver l'utilisateur actuel dans la liste des attendees
            const userEmail = this.googleUserProfile?.mail;
            const attendeeIndex = eventResponse.attendees.findIndex(
                (attendee: any) =>
                    attendee.email === userEmail && attendee.self === true,
            );

            if (attendeeIndex === -1) {
                logger.error(
                    LOGS.GoogleManager,
                    "Current user not found in attendees",
                );
                throw "Current user not found in attendees";
            }

            // 3. Mettre à jour le responseStatus de l'utilisateur
            eventResponse.attendees[attendeeIndex].responseStatus = "accepted";

            // 4. Envoyer la mise à jour avec la structure complète
            const body = {
                attendees: eventResponse.attendees,
            };
            await this.callGoogleApi(
                `${calendarApiUrl}/calendars/primary/events/${itemId}?sendNotifications=true`,
                "PATCH",
                body,
            );

            logger.info(
                LOGS.GoogleManager,
                "Agenda Item accepted successfully",
            );
            return true;
        } catch (error) {
            logger.error(
                LOGS.GoogleManager,
                `Error in acceptAgendaItem: ${error}`,
            );
            return false;
        }
    }

    public async tentativelyAcceptAgendaItem(itemId: string): Promise<boolean> {
        logger.info(
            LOGS.GoogleManager,
            `tentativelyAcceptAgendaItem(${itemId})`,
        );

        try {
            // 1. Récupérer l'événement existant
            const eventResponse: Schema$Event = await this.callGoogleApi(
                `${calendarApiUrl}/calendars/primary/events/${itemId}`,
            );

            if (
                !eventResponse.attendees ||
                !Array.isArray(eventResponse.attendees)
            ) {
                logger.error(LOGS.GoogleManager, "No attendees found in event");
                throw "Unexpected attendees found in event";
            }

            // 2. Trouver l'utilisateur actuel dans la liste des attendees
            const userEmail = this.googleUserProfile?.mail;
            const attendeeIndex = eventResponse.attendees.findIndex(
                (attendee: any) =>
                    attendee.email === userEmail && attendee.self === true,
            );

            if (attendeeIndex === -1) {
                logger.error(
                    LOGS.GoogleManager,
                    "Current user not found in attendees",
                );
                throw "Current user not found in attendees";
            }

            // 3. Mettre à jour le responseStatus de l'utilisateur
            eventResponse.attendees[attendeeIndex].responseStatus = "tentative";

            // 4. Envoyer la mise à jour avec la structure du body
            const body = {
                attendees: eventResponse.attendees,
            };
            const eventUpdated: Schema$Event = await this.callGoogleApi(
                `${calendarApiUrl}/calendars/primary/events/${itemId}?sendNotifications=true`,
                "PATCH",
                body,
            );

            logger.info(
                LOGS.GoogleManager,
                "Agenda Item accepted tentatively successfully",
            );
            return true;
        } catch (error) {
            logger.error(
                LOGS.GoogleManager,
                `Error in tentativelyAcceptAgendaItem: ${error}`,
            );
            return false;
        }
    }

    public async declineAgendaItem(itemId: string): Promise<boolean> {
        logger.info(LOGS.GoogleManager, `declineAgendaItem(${itemId})`);

        try {
            // 1. Récupérer l'événement existant
            const eventResponse: Schema$Event = await this.callGoogleApi(
                `${calendarApiUrl}/calendars/primary/events/${itemId}`,
            );

            if (
                !eventResponse.attendees ||
                !Array.isArray(eventResponse.attendees)
            ) {
                logger.error(LOGS.GoogleManager, "No attendees found in event");
                throw "Unexpected attendees found in event";
            }

            // 2. Trouver l'utilisateur actuel dans la liste des attendees
            const userEmail = this.googleUserProfile?.mail;
            const attendeeIndex = eventResponse.attendees.findIndex(
                (attendee: any) =>
                    attendee.email === userEmail && attendee.self === true,
            );

            if (attendeeIndex === -1) {
                logger.error(
                    LOGS.GoogleManager,
                    "Current user not found in attendees",
                );
                throw "Current user not found in attendees";
            }

            // 3. Mettre à jour le responseStatus de l'utilisateur
            eventResponse.attendees[attendeeIndex].responseStatus = "declined";

            // 4. Envoyer la mise à jour avec la structure du body
            const body = {
                attendees: eventResponse.attendees,
            };
            await this.callGoogleApi(
                `${calendarApiUrl}/calendars/primary/events/${itemId}?sendNotifications=true`,
                "PATCH",
                body,
            );

            logger.info(
                LOGS.GoogleManager,
                "Agenda Item declined successfully",
            );
            return true;
        } catch (error) {
            logger.error(
                LOGS.GoogleManager,
                `Error in declineAgendaItem: ${error}`,
            );
            return false;
        }
    }

    public async getUserAgendaModifiedSince(
        sinceDate: string,
    ): Promise<Event[]> {
        logger.info(
            LOGS.GoogleManager,
            `getUserAgendaModifiedSince(${sinceDate})`,
        );

        try {
            const selectedFields =
                "fields=items(id,summary,description,start,end,organizer,attendees,location,conferenceData),nextPageToken";
            const googleEventsList: Schema$Events = await this.callGoogleApi(
                `${calendarApiUrl}/calendars/primary/events?` +
                    `updatedMin=${sinceDate}&` +
                    `maxResults=1000&` +
                    selectedFields,
            );

            if (!googleEventsList.items) {
                throw "No items on user agenda requested";
            }
            const googleEventsItems = googleEventsList.items;

            // Mapper Google Events vers Graph Events
            const mapGoogleEventToEvent: Event[] = googleEventsItems.map(
                (googleEvent: Schema$Event) =>
                    this._mapGoogleEventToGraphEvent(googleEvent),
            );
            logger.info(
                LOGS.GoogleManager,
                "Mapper Google Events vers Graph Events",
                mapGoogleEventToEvent,
            );

            return mapGoogleEventToEvent;
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager],
                `Error in getUserAgendaModifiedSince: ${error}`,
            );
            throw new Error(
                `Error in getUserAgendaModifiedSince: ${error.message}`,
            );
        }
    }

    //#endregion Agenda

    //#region Documents
    public async getRecentDocuments(
        numberOfDocuments: number = 20,
    ): Promise<DriveItem[]> {
        logger.info(
            LOGS.GoogleManager,
            `getRecentDocuments(${numberOfDocuments})`,
        );

        try {
            // Utilisation de l'API Google Drive Files.list avec orderBy viewedByMeTime
            const driveRequest =
                `${driveApiUrl}/files?` +
                `pageSize=${numberOfDocuments}&` +
                `orderBy=viewedByMeTime desc&` +
                `fields=files(id,name,description,createdTime,modifiedTime,owners,lastModifyingUser,parents,viewedByMeTime)&` +
                `q=trashed=false`;

            const fileListResponse: Schema$FileList =
                await this.callGoogleApi(driveRequest);

            if (!fileListResponse.files) {
                throw "Unexpected undefined files on get documents";
            }

            logger.info(
                [LOGS.GoogleManager, LOGS.AnalysisLifecycle],
                `${fileListResponse.files.length} documents processed`,
            );

            // Conversion des données Google Drive vers le format DriveItem
            const resultDriveItem: DriveItem[] = fileListResponse.files.map(
                (file: Schema$File) =>
                    this._mapGoogleFileToGraphDriveItem(file),
            );
            logger.info(LOGS.GoogleManager, "Drive Item ", resultDriveItem);

            return resultDriveItem;
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager, LOGS.AnalysisLifecycle],
                `Error in getRecentDocuments Google Drive : ${error}`,
            );
            throw new Error(
                `Error in getRecentDocuments Google Drive : ${error}`,
            );
        }
    }
    //#endregion

    //#region Divers
    public async getGraphData(requestUrl: string): Promise<any> {
        logger.info(LOGS.GoogleManager, `getGraphData(${requestUrl})`);
        logger.error(
            LOGS.GoogleManager,
            "Method getGraphData() is not implemented",
        );
        const fakeResult: any = {};
        return fakeResult;
    }
    //#endregion

    // #endregion Public Methods

    // #region Private Methods

    private async callGoogleApi(
        endpoint: string,
        method: string = "GET",
        body?: any,
        isBatchRequest: boolean = false,
        retrying: boolean = false,
    ): Promise<any> {
        const accessToken = await this.authContext.getProviderToken();

        let headers: { [key: string]: string } = {};
        if (isBatchRequest) {
            headers = {
                "Content-Type": `multipart/mixed; boundary=${this.boundaryForBatchRequest}`, // Les requêtes batch utilisent le type multipart/mixed qui DOIT avoir un paramètre boundary
            };
        } else {
            headers = {
                "Content-Type": "application/json",
            };
        }

        const options: RequestInit = {
            method,
            headers: {
                Authorization: `Bearer ${accessToken}`,
                ...headers,
            },
        };

        if (body) {
            options.body = isBatchRequest ? body : JSON.stringify(body);
        }

        try {
            logger.info(
                LOGS.GoogleManager,
                `Calling Google API ${endpoint} with method ${method}`,
            );

            // Retry 429 borné et unifié (helper partagé) : Retry-After clampé [0,30s],
            // sinon backoff exponentiel + jitter, max 3 tentatives, puis ThrottleError typée.
            // Remplace l'ancienne récursion NON bornée. Le 401 garde sa propre récursion
            // (refresh token), hors de ce wrapper qui rejoue avec le même token.
            const response = await withThrottleRetry(
                () => fetch(endpoint, options),
                {
                    endpoint,
                    logCategory: LOGS.GoogleManager,
                },
            );

            // 401 Unauthorized — provider token périmé. Force un refresh (invalide le cache
            // provider token) et retente une fois. La récursion re-fetche un token frais en tête.
            if (response.status === 401 && !retrying) {
                logger.warn(
                    LOGS.GoogleManager,
                    `Received 401 from Google API (${endpoint}) — provider token outdated, forcing refresh`,
                );
                await this.authContext.forceRefresh();
                logger.warn(
                    LOGS.GoogleManager,
                    `  => Token refreshed, retrying ${endpoint}`,
                );
                return this.callGoogleApi(
                    endpoint,
                    method,
                    body,
                    isBatchRequest,
                    true,
                );
            }
            if (response.status === 401 && retrying) {
                logger.error(
                    LOGS.GoogleManager,
                    `Second 401 on ${endpoint} after retry — session rejected`,
                );
                this.eventBus.emit("auth-token-rejected", {
                    source: "GoogleManager.callGoogleApi",
                });
                throw new Error(
                    `Google API 401 Unauthorized — session revoked after retry (${endpoint})`,
                );
            }

            // Gestion d'une réponse vide
            if (response.status === 204) {
                return { success: true, empty: true };
            }

            if (!response.ok) {
                // AB#1764 — attacher le status HTTP pour détection fiable en amont (404 idempotent).
                throw makeHttpError(
                    `HTTP error! status: ${response.status}`,
                    response.status,
                );
            }

            logger.info(
                [LOGS.GoogleManager],
                `Returning Google API result (${endpoint.slice(0, 30)})`,
                response,
            );

            // if batch request return brut text of response, NOT json
            if (isBatchRequest) {
                return response;
            }

            const result = await response.json();
            return result;
        } catch (error) {
            logger.error(
                LOGS.GoogleManager,
                `Error in Google API call to ${endpoint}: ${error}`,
            );
            throw error;
        }
    }

    /**
     * Parse la réponse batch de Gmail
     */
    private _parseBatchResponse(responseText: string): Schema$Message[] {
        logger.info(
            [LOGS.GoogleManager, LOGS.AnalysisLifecycle],
            `log _parseBatchResponse()...`,
        );

        const results: Schema$Message[] = [];

        try {
            // Divise la réponse par les boundaries
            const parts = responseText.split(/--batch_[^\r\n]+/);

            for (const part of parts) {
                if (part.trim() && part.includes("HTTP/1.1")) {
                    // Vérifier le status HTTP
                    const statusMatch = part.match(/HTTP\/1\.1 (\d+)/);
                    const statusCode = statusMatch
                        ? parseInt(statusMatch[1])
                        : 0;

                    // Succès - Extrait le JSON de la réponse
                    if (statusCode >= 200 && statusCode < 300) {
                        const jsonStart = part.indexOf("{");
                        const jsonEnd = part.lastIndexOf("}") + 1;

                        if (jsonStart !== -1 && jsonEnd > jsonStart) {
                            try {
                                const jsonContent = part.substring(
                                    jsonStart,
                                    jsonEnd,
                                );
                                const parsedContent = JSON.parse(jsonContent);

                                // Vérifie que c'est un message valide
                                if (parsedContent.id) {
                                    results.push(parsedContent);
                                }
                            } catch (parseError) {
                                logger.error(
                                    [LOGS.GoogleManager],
                                    `Error parsing batch response part: ${parseError}`,
                                );
                                throw `Error parsing batch response: ${parseError}`;
                            }
                        }
                    }
                    // Echec - statusCode différent de 200, log le contenu retourné
                    else {
                        logger.warn(LOGS.GoogleManager, `part: ${part}`);
                    }
                }
            }
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager],
                `Error parsing batch response: ${error}`,
            );
            throw `Error parsing batch response: ${error}`;
        }

        logger.info(
            LOGS.GoogleManager,
            `log _parseBatchResponse() results :`,
            results,
        );
        return results;
    }

    /**
     * Parse la réponse batch spécifique aux labels Gmail
     */
    private _parseBatchLabelsResponse(responseText: string): Schema$Label[] {
        logger.info([LOGS.GoogleManager], `_parseBatchLabelsResponse()`);

        const results: any[] = [];

        try {
            // Divise la réponse par les boundaries
            const parts = responseText.split(/--batch_[^\r\n]+/);

            for (const part of parts) {
                if (part.trim() && part.includes("HTTP/1.1")) {
                    // Extrait le JSON de chaque partie
                    const jsonStart = part.indexOf("{");
                    const jsonEnd = part.lastIndexOf("}") + 1;

                    if (jsonStart !== -1 && jsonEnd > jsonStart) {
                        try {
                            const jsonContent = part.substring(
                                jsonStart,
                                jsonEnd,
                            );
                            const parsedContent = JSON.parse(jsonContent);

                            // Vérifie que c'est un label valide
                            if (
                                parsedContent.id &&
                                parsedContent.name !== undefined
                            ) {
                                results.push(parsedContent);
                            }
                        } catch (parseError) {
                            logger.error(
                                [LOGS.GoogleManager],
                                `Error parsing batch label response part: ${parseError}`,
                            );
                        }
                    }
                }
            }
        } catch (error) {
            logger.error(
                [LOGS.GoogleManager],
                `Error parsing batch labels response: ${error}`,
            );
        }

        logger.info(
            LOGS.GoogleManager,
            `_parseBatchLabelsResponse results: ${results.length} labels`,
        );
        return results;
    }

    /**
     * Convertit un message Gmail vers le type Mail
     */
    private _convertGmailToMail(gmailMessage: Schema$Message): Mail {
        const headers = this._extractHeaders(
            gmailMessage.payload?.headers || [],
        );

        // const attachments        = this._mapGmailAttachments(gmailMessage);
        const linkedEventDetails = this._mapGmailLinkedEventDetails(
            gmailMessage,
            headers,
        );

        return {
            id: gmailMessage.id!,
            internetMessageId: gmailMessage.id!, // headers.messageId || '', // For now we use message id not internet message id for Google
            conversationId: gmailMessage.threadId || "",
            parentFolderId: this._extractParentFolder(
                gmailMessage.labelIds || [],
            ),
            subject: headers.subject || "",
            message:
                this._extractBody(gmailMessage.payload, "text/html") ||
                this._extractBody(gmailMessage.payload, "text/plain") ||
                "",
            uniqueBody:
                this._extractBody(gmailMessage.payload, "text/html") ||
                this._extractBody(gmailMessage.payload, "text/plain") ||
                "",
            messagePreview: gmailMessage.snippet || "",
            isRead: gmailMessage.labelIds?.includes("UNREAD") === false,
            from: this._mapGmailSender(headers.from),
            to: this._mapGmailRecipients(headers.to),
            cc: this._mapGmailRecipients(headers.cc),
            bcc: this._mapGmailRecipients(headers.bcc),
            labelIds: gmailMessage.labelIds || [],
            isStarred: gmailMessage.labelIds?.includes("STARRED") || false,
            isImportant: gmailMessage.labelIds?.includes("IMPORTANT") || false,
            sentDateTime: this._mapGmailDateToIDateValue(
                gmailMessage.internalDate,
            ),
            attachments: [],
            isUserProcessed: false,
            isUserEdited: false,
            componentSize: MailComponentSize.SMALL,
            linkedEventDetails: linkedEventDetails,
            isFocus: false,
            smartLabel: undefined,
            hasAttachment:
                gmailMessage.payload && gmailMessage.payload.filename
                    ? true
                    : false,
            hasLinkedMeeting: linkedEventDetails != undefined,
            links: [],
            score: null,
            manualFocusDate: null,
            sentDateTimeMailvista: null,
            bodyContentType: this._extractBody(
                gmailMessage.payload,
                "text/html",
            )
                ? BodyContentType.HTML
                : BodyContentType.TEXT,
            uniqueBodyContentType: this._extractBody(
                gmailMessage.payload,
                "text/html",
            )
                ? BodyContentType.HTML
                : BodyContentType.TEXT,
        };
    }

    /**
     * Convertit les données d'un fichier Google Drive vers Microsoft Drive
     *
     * @param file - Données d'un fichier Google Drive
     * @returns - Retourne le fichier entrant typé sous Graph
     */
    private _mapGoogleFileToGraphDriveItem(file: Schema$File): DriveItem {
        return {
            id: file.id || "",
            name: file.name || "",
            description: file.description || undefined,
            createdDateTime: file.createdTime || "",
            lastModifiedDateTime: file.modifiedTime || "",
            createdBy: file.owners?.[0]
                ? {
                      user: {
                          displayName: file.owners[0].displayName,
                      },
                  }
                : undefined,
            lastModifiedBy: file.lastModifyingUser
                ? {
                      user: {
                          displayName: file.lastModifyingUser.displayName,
                      },
                  }
                : undefined,
            parentReference: file.parents?.[0]
                ? {
                      id: file.parents[0],
                  }
                : undefined,
            fileSystemInfo: {
                lastAccessedDateTime: file.viewedByMeTime,
                lastModifiedDateTime: file.modifiedTime,
            },
        };
    }

    /**
     * Extrait les headers importants du message Gmail
     */
    private _extractHeaders(headers: Schema$MessagePartHeader[]): {
        [key: string]: string;
    } {
        const headerMap: { [key: string]: string } = {};

        headers.forEach((header) => {
            if (header.name && header.value) {
                switch (header.name.toLowerCase()) {
                    case "message-id":
                        headerMap.messageId = header.value;
                        break;
                    case "subject":
                        headerMap.subject = header.value;
                        break;
                    case "from":
                        headerMap.from = header.value;
                        break;
                    case "to":
                        headerMap.to = header.value;
                        break;
                    case "cc":
                        headerMap.cc = header.value;
                        break;
                    case "bcc":
                        headerMap.bcc = header.value;
                        break;
                    case "date":
                        headerMap.date = header.value;
                        break;
                }
            }
        });

        return headerMap;
    }

    /**
     * Extrait le contenu du message selon le type MIME
     */
    private _extractBody(
        payload: Schema$MessagePart | undefined,
        mimeType: string,
    ): string | null {
        if (!payload) return null;

        // Si c'est le bon type MIME et qu'il y a du contenu
        if (payload.mimeType === mimeType && payload.body?.data) {
            return decodeBase64URL(payload.body.data);
        }

        // Recherche récursive dans les parties
        if (payload.parts) {
            for (const part of payload.parts) {
                const result = this._extractBody(part, mimeType);
                if (result) return result;
            }
        }

        return null;
    }

    /**
     * Extrait le dossier parent basé sur les labels Gmail
     */
    private _extractParentFolder(labelIds: string[]): string {
        // Gmail utilise des labels système
        // Retourne l'équivalent des dossiers Outlook
        if (labelIds.includes("INBOX")) return MailFolder.Inbox;
        if (labelIds.includes("SENT")) return MailFolder.SentItems;
        if (labelIds.includes("DRAFT")) return MailFolder.Drafts;
        if (labelIds.includes("TRASH")) return MailFolder.Deleteditems;
        if (labelIds.includes("SPAM")) return MailFolder.JunkEmail;

        // Retour par défaut
        return MailFolder.Inbox;
    }

    /**
     * Mappe un expéditeur Gmail vers MailSender
     */
    private _mapGmailSender(fromHeader: string | undefined): MailSender {
        if (!fromHeader) {
            return {
                name: "Inconnu",
                email: "Inconnu",
                avatarUrl: null,
            };
        }

        // Parse "Nom <email@example.com>" ou juste "email@example.com"
        const emailRegex = /^(?:"?([^"]*)"?\s)?(?:<?([^<>\s]+@[^<>\s]+)>?)$/;
        const match = fromHeader.match(emailRegex);

        if (match) {
            const name =
                match[1]?.trim() || match[2]?.split("@")[0] || "Inconnu";
            const email = match[2] || "Inconnu";

            return {
                name: name,
                email: email,
                avatarUrl: null,
            };
        }

        return {
            name: fromHeader,
            email: fromHeader,
            avatarUrl: null,
        };
    }

    /**
     * Mappe les destinataires Gmail vers MailSender[]
     */
    private _mapGmailRecipients(
        recipientsHeader: string | undefined,
    ): MailSender[] {
        if (!recipientsHeader) return [];

        // Divise par virgule et mappe chaque destinataire
        return recipientsHeader
            .split(",")
            .map((recipient) => this._mapGmailSender(recipient.trim()))
            .filter((sender) => sender.email !== "Inconnu");
    }

    /**
     * Mappe les pièces jointes Gmail
     */
    private _mapGmailAttachments(message: Schema$Message): MailAttachment[] {
        const attachments: MailAttachment[] = [];

        if (!message || !message.payload) return attachments;

        this._extractAttachmentsFromParts(message, attachments);

        return attachments;
    }

    /**
     * Extrait les pièces jointes d'un mail
     *
     * @param parts - Contenu du payload du message en plusieurs parties
     * @param attachments - Tableau qui contient les données des pièces jointes
     */
    private _extractAttachmentsFromParts(
        message: Schema$Message,
        attachments: any[],
    ) {
        logger.info(
            [LOGS.GoogleManager],
            `log _extractAttachmentsFromParts from message : ${message.id}`,
        );

        try {
            if (message.payload?.parts) {
                message.payload.parts.forEach(
                    async (part: Schema$MessagePart) => {
                        // Vérifie si la part a un filename non vide
                        if (part.filename && part.filename.trim() !== "") {
                            // Récupère la data de le PJ
                            const request = `${gmailApiUrl}/users/me/messages/${message.id}/attachments/${part.body?.attachmentId}?fields=data`;
                            const response = await this.callGoogleApi(request);

                            // !!! l'api pour récupérer une pièce jointe retourne un string en base64url
                            // Nécessaire de le décoder pour le traiter correctement
                            const imgAsBase64Strandard =
                                convertBase64URLToBinary(response.data);

                            const attachment = {
                                id: part.body?.attachmentId || "",
                                name: part.filename,
                                size: part.body?.size || 0,

                                // nécessaire pour FileAttachment[]
                                contentId: part.body?.attachmentId || "",
                                contentType:
                                    part.mimeType || "application/octet-stream",
                                contentBytes: imgAsBase64Strandard,

                                // nécessaire pour MailAttachment[]
                                path: "",
                                preview: `data:${part.mimeType};base64,${imgAsBase64Strandard}`,
                                type:
                                    part.mimeType || "application/octet-stream",
                                createdAt: this._mapGmailDateToIDateValue(
                                    message.internalDate,
                                ),
                                modifiedAt: this._mapGmailDateToIDateValue(
                                    message.internalDate,
                                ),
                                sentAt: this._mapGmailDateToIDateValue(
                                    message.internalDate,
                                ),
                            };

                            logger.info(
                                LOGS.GoogleManager,
                                "Attachment constructed",
                                attachment,
                            );

                            attachments.push(attachment);
                        }

                        // Recherche récursive dans les sous-parties si elles existent
                        //
                        // if (part.parts && Array.isArray(part.parts)) {
                        //     this._extractAttachmentsFromParts(part.parts, attachments);
                        // }
                    },
                );
            }

            return attachments;
        } catch (error) {
            logger.error(
                LOGS.GoogleManager,
                `Error during extract attachment from mail ${message.id}: ${error}`,
            );
            throw new Error(
                `Error during extract attachment from mail ${message.id}: ${error}`,
            );
        }
    }

    /**
     * Convertit la date Gmail vers IDateValue
     */
    private _mapGmailDateToIDateValue(
        internalDate: string | null | undefined,
    ): IDateValue {
        if (!internalDate) return new Date().toISOString();

        // internalDate est en millisecondes depuis epoch
        const date = new Date(parseInt(internalDate));
        return date.toISOString();
    }

    /**
     * Mappe les détails d'événement Gmail (limité car Gmail n'expose pas autant de détails que Graph)
     */
    private _mapGmailLinkedEventDetails(
        gmailMessage: Schema$Message,
        headers: { [key: string]: string },
    ): LinkedEventDetails | undefined {
        // TODO - Revoir implémenntation avec des vraies données
        // Gmail ne fournit pas directement les détails d'événement comme Graph API
        // Cette implémentation est basique et pourrait nécessiter des améliorations
        logger.warn(
            LOGS.GoogleManager,
            "Implemented with fake data, to rework",
        );

        const subject = headers.subject || "";

        // Détection basique d'invitation (patterns courants)
        const isInvitation =
            subject.toLowerCase().includes("invitation") ||
            subject.toLowerCase().includes("meeting") ||
            subject.toLowerCase().includes("rendez-vous") ||
            subject.toLowerCase().includes("réunion");

        if (!isInvitation) return undefined;

        return {
            id: gmailMessage.id || "",
            meetingMessageType: "meetingRequest",
            meetingRequestType: "newMeetingRequest",
            subject: subject,
            bodyPreview: "", // TODO - à implémenter
            startDateTime: new Date().toISOString(), // Pas disponible dans Gmail API basique
            endDateTime: new Date().toISOString(), // Pas disponible dans Gmail API basique
            isAllDay: false,
            recurrence: false,
            responseRequested: true,
            isDelegated: false,
            location: {
                displayName: "",
                locationType: "",
                uniqueIdType: "",
            },
            userResponseStatus: AttendeeResponseStatus.NONE,
            isExpired: false,
            isCancelled: false,
            messageType: EventMessageType.EVENT_MESSAGE_REQUEST,
            organizerMail: "", // TODO - à implémenter (checker le champs creator dans type Schema$Message)
        };
    }

    private _mapGoogleEventToGraphEvent(googleEvent: Schema$Event): Event {
        // Ligne de participant de l'utilisateur lui-même : sa réponse à l'invitation (F-1.12).
        const selfAttendee = googleEvent.attendees?.find(
            (attendee) => attendee.self,
        );

        return {
            id: googleEvent.id || "",
            subject: googleEvent.summary || "",
            start: this._mapGoogleDateTimeToGraphDateTime(googleEvent.start),
            end: this._mapGoogleDateTimeToGraphDateTime(googleEvent.end),
            isAllDay: !!googleEvent.start?.date, // Si date définie (au lieu de dateTime), c'est un événement toute la journée
            location: googleEvent.location
                ? {
                      displayName: googleEvent.location,
                      locationUri: null,
                      address: null,
                      coordinates: null,
                      locationEmailAddress: null,
                      locationType: null,
                      uniqueId: null,
                      uniqueIdType: null,
                  }
                : null,
            organizer: googleEvent.organizer
                ? {
                      emailAddress: {
                          name:
                              googleEvent.organizer.displayName ||
                              googleEvent.organizer.email ||
                              "",
                          address: googleEvent.organizer.email || "",
                      },
                  }
                : null,
            attendees:
                googleEvent.attendees?.map((attendee) => ({
                    emailAddress: {
                        name: attendee.displayName || attendee.email || "",
                        address: attendee.email || "",
                    },
                    status: {
                        response: this._mapGoogleResponseStatusToGraph(
                            attendee.responseStatus || undefined,
                        ),
                        time: null,
                    },
                    type: null,
                })) || [],
            onlineMeetingUrl:
                googleEvent.conferenceData?.entryPoints?.find(
                    (ep) => ep.entryPointType === "video",
                )?.uri || null,
            // Propriétés optionnelles avec valeurs par défaut
            body: null,
            bodyPreview: googleEvent.description || "",
            importance: null,
            // Ce qui occupe vraiment le créneau (F-1.12) : `status` et `transparency` ne sont lus
            // que par `getUserAgenda` ; ailleurs, l'événement reste non annulé et occupé.
            isCancelled: googleEvent.status === "cancelled",
            isDraft: false,
            isOnlineMeeting: !!googleEvent.conferenceData,
            isOrganizer: !!googleEvent.organizer?.self,
            isReminderOn: false,
            reminderMinutesBeforeStart: null,
            responseRequested: null,
            responseStatus: selfAttendee
                ? {
                      response: this._mapGoogleResponseStatusToGraph(
                          selfAttendee.responseStatus || undefined,
                      ),
                      time: null,
                  }
                : null,
            sensitivity: null,
            // « Disponible » dans Google Agenda (`transparent`) : libre ; sinon occupé.
            showAs:
                googleEvent.transparency === "transparent" ? "free" : "busy",
            webLink: googleEvent.htmlLink || null,
            categories: [],
            changeKey: null,
            createdDateTime: googleEvent.created || null,
            lastModifiedDateTime: googleEvent.updated || null,
        };
    }

    private _mapGoogleDateTimeToGraphDateTime(
        googleDateTime?: Schema$EventDateTime,
    ): DateTimeTimeZone | null {
        if (!googleDateTime) return null;

        // Pour les événements toute la journée : minuit du jour du calendrier, sans « Z », comme
        // Graph rend ses journées entières. Les écrans d'agenda la lisent ainsi au jour local ;
        // à minuit UTC, elle tombait à 01:00 ou 02:00 à Paris (et débordait sur le lendemain),
        // ou la veille sur le continent américain (F-1.12).
        if (googleDateTime.date) {
            return {
                dateTime: `${googleDateTime.date}T00:00:00.0000000`,
                timeZone: "UTC", // Utiliser UTC au lieu de timeZone problématique
            };
        }

        // Pour les événements avec heure spécifique
        if (googleDateTime.dateTime) {
            // Convertir en UTC pour éviter les problèmes de timezone
            const date = new Date(googleDateTime.dateTime);
            return {
                dateTime: date.toISOString(),
                timeZone: "UTC", // Toujours utiliser UTC pour éviter l'erreur de timezone
            };
        }

        return null;
    }

    private _mapGoogleResponseStatusToGraph(
        googleStatus?: string,
    ): ResponseType | null {
        if (!googleStatus) return null;

        switch (googleStatus) {
            case "accepted":
                return "accepted";
            case "declined":
                return "declined";
            case "tentative":
                return "tentativelyAccepted";
            case "needsAction":
                return "notResponded";
            default:
                return "none";
        }
    }

    private _getHeader(headers: any, name: string): string {
        const header = headers.find(
            (h: any) => h.name.toLowerCase() === name.toLowerCase(),
        );
        return header?.value || "";
    }

    /**
     * Méthode commune pour traiter les réponses d'emails
     * Construit le corps du mail avec les en-têtes nécessaires
     *
     * @param originalMessage - Message auquel répondre
     * @param mailData - Contenu du mail de réponse
     * @param action - reply attendu
     * @param replyAll - Boolean dans le cas d'un répondre à tous
     * @returns
     */
    private _buildMailResponse(
        originalMessage: Schema$Message,
        mailData: ReplyForward,
        action: "reply",
        replyAll: boolean = false,
    ): {
        mailSendData: MailSend;
        inReplyTo: string | undefined;
        references: string | undefined;
    } {
        logger.info(
            LOGS.GoogleManager,
            `log _processMail : ${action} the message ${originalMessage.id}`,
        );

        try {
            // Extraire les informations du message original
            const headers = originalMessage.payload?.headers || [];

            const originalFrom = this._getHeader(headers, "From");
            const originalTo = this._getHeader(headers, "To");
            const originalCc = this._getHeader(headers, "Cc");
            const originalBcc = this._getHeader(headers, "Bcc");
            const originalSubject = this._getHeader(headers, "Subject");
            const originalMessageId = this._getHeader(headers, "Message-ID");
            const originalReferences = this._getHeader(headers, "References");

            const recipients = this._buildRecipientLists(
                action,
                replyAll,
                { originalFrom, originalTo, originalCc, originalBcc },
                mailData,
            );

            // Construire le sujet
            const subject = mailData.subject || originalSubject;

            // Construire le contenu
            const content = this._buildContent(action, mailData.content);

            // Construire les en-têtes References et In-Reply-To
            let inReplyTo: string | undefined;
            let references: string | undefined;

            if (action === "reply" && originalMessageId) {
                inReplyTo = originalMessageId;
                references = originalReferences
                    ? `${originalReferences} ${originalMessageId}`
                    : originalMessageId;
            }

            // Créer l'objet MailSend
            const mailSendData: MailSend = {
                toRecipients: recipients.to,
                ccRecipients: recipients.cc.length > 0 ? recipients.cc : [],
                bccRecipients: recipients.bcc.length > 0 ? recipients.bcc : [],
                subject,
                content,
                contentType: mailData.contentType || "HTML",
                attachments: mailData.attachments || [],
            };

            return { mailSendData, inReplyTo, references };
        } catch (error) {
            logger.error([LOGS.GoogleManager], `Error in ${action}: ${error}`);
            throw new Error(` !!! Error in ${action}: ${error}`);
        }
    }

    /**
     * Construit les listes de destinataires
     */
    private _buildRecipientLists(
        action: "reply",
        replyAll: boolean,
        originalHeaders: {
            originalFrom: string;
            originalTo: string;
            originalCc: string;
            originalBcc: string;
        },
        mailData: ReplyForward,
    ): { to: string[]; cc: string[]; bcc: string[] } {
        logger.info(LOGS.GoogleManager, `log _buildRecipientLists`);

        let toRecipients: string[] = [];
        let ccRecipients: string[] = [];
        let bccRecipients: string[] = [];

        if (action === "reply") {
            const userEmail = this.googleUserProfile?.mail || "";

            if (replyAll) {
                // Répondre à tous : inclure tous les destinataires originaux sauf soi-même
                if (originalHeaders.originalFrom) {
                    const fromEmail = this._extractEmailFromHeader(
                        originalHeaders.originalFrom,
                    );
                    if (fromEmail && fromEmail !== userEmail) {
                        toRecipients.push(fromEmail);
                    }
                }

                if (originalHeaders.originalTo) {
                    const toEmails = this._extractEmailsFromHeader(
                        originalHeaders.originalTo,
                    );
                    toEmails.forEach((email) => {
                        if (
                            email !== userEmail &&
                            !toRecipients.includes(email)
                        ) {
                            toRecipients.push(email);
                        }
                    });
                }

                if (originalHeaders.originalCc) {
                    const ccEmails = this._extractEmailsFromHeader(
                        originalHeaders.originalCc,
                    );
                    ccEmails.forEach((email) => {
                        if (
                            email !== userEmail &&
                            !toRecipients.includes(email)
                        ) {
                            ccRecipients.push(email);
                        }
                    });
                }

                if (originalHeaders.originalBcc) {
                    const bccEmails = this._extractEmailsFromHeader(
                        originalHeaders.originalBcc,
                    );
                    bccEmails.forEach((email) => {
                        if (
                            email !== userEmail &&
                            !toRecipients.includes(email) &&
                            !ccRecipients.includes(email)
                        ) {
                            bccRecipients.push(email);
                        }
                    });
                }
            } else {
                // Répondre simple : seulement à l'expéditeur
                if (originalHeaders.originalFrom) {
                    const fromEmail = this._extractEmailFromHeader(
                        originalHeaders.originalFrom,
                    );
                    if (fromEmail) {
                        toRecipients.push(fromEmail);
                    }
                }
            }
        }

        // Fusionner avec les destinataires spécifiés dans mailData
        if (mailData.toRecipients) {
            mailData.toRecipients.forEach((email) => {
                if (!toRecipients.includes(email)) {
                    toRecipients.push(email);
                }
            });
        }
        logger.info(LOGS.GoogleManager, "mail toRecipients", toRecipients);

        if (mailData.ccRecipients) {
            mailData.ccRecipients.forEach((email) => {
                if (!ccRecipients.includes(email)) {
                    ccRecipients.push(email);
                }
            });
        }
        logger.info(LOGS.GoogleManager, "mail ccRecipients", ccRecipients);

        if (mailData.bccRecipients) {
            mailData.bccRecipients.forEach((email) => {
                if (!bccRecipients.includes(email)) {
                    bccRecipients.push(email);
                }
            });
        }
        logger.info(LOGS.GoogleManager, "mail bccRecipients", bccRecipients);

        return { to: toRecipients, cc: ccRecipients, bcc: bccRecipients };
    }

    private _buildContent(action: "reply", newContent: string): string {
        logger.info(
            LOGS.GoogleManager,
            `log _buildContent: ${action} with content ${newContent}...`,
        );

        return newContent;
    }

    /**
     * Convertit l'objet reçu en message RFC2822 pour envoie par Gmail
     * Ajout les en-têtes nécessaires pour les réponses
     *
     * @param mailSend - Structure pour l'envoi d'un nouvel email
     * @param inReplyTo - (optionnel) MessageId dans le cas d'une réponse
     * @param references - (optionnel)
     * @param threadId
     * @returns - Message converti en RFC2822 puis en base64url
     */
    private _createRFC2822Message(
        mailSend: MailSend,
        inReplyTo?: string,
        references?: string,
        threadId?: string,
    ): string {
        // Separate inline and regular attachments
        const inlineAttachments =
            mailSend.attachments?.filter((att) => att.isInline) || [];
        const regularAttachments =
            mailSend.attachments?.filter((att) => !att.isInline) || [];

        // Determine boundaries
        const mixedBoundary = `----boundary_mixed_${Date.now()}`;
        const relatedBoundary = `----boundary_related_${Date.now()}`;

        // Construction des en-têtes RFC2822
        let message = "";
        message += `To: ${mailSend.toRecipients.join(", ")}\r\n`;

        if (mailSend.ccRecipients && mailSend.ccRecipients.length > 0) {
            message += `Cc: ${mailSend.ccRecipients.join(", ")}\r\n`;
        }

        if (mailSend.bccRecipients && mailSend.bccRecipients.length > 0) {
            message += `Bcc: ${mailSend.bccRecipients.join(", ")}\r\n`;
        }

        // En-têtes spécifiques
        if (inReplyTo) {
            message += `In-Reply-To: ${inReplyTo}\r\n`;
        }

        if (references) {
            message += `References: ${references}\r\n`;
        }

        if (threadId) {
            message += `X-Gmail-Thread-Id: ${threadId}\r\n`;
        }

        message += `Subject: ${mailSend.subject}\r\n`;
        message += `Date: ${new Date().toUTCString()}\r\n`;
        message += `MIME-Version: 1.0\r\n`;

        // Determine content structure based on attachments
        const hasRegularAttachments = regularAttachments.length > 0;
        const hasInlineAttachments = inlineAttachments.length > 0;

        if (hasRegularAttachments) {
            // Use multipart/mixed as top level
            message += `Content-Type: multipart/mixed; boundary="${mixedBoundary}"\r\n\r\n`;
            message += `--${mixedBoundary}\r\n`;
        }

        if (hasInlineAttachments) {
            // Use multipart/related for inline images
            message += `Content-Type: multipart/related; boundary="${relatedBoundary}"\r\n\r\n`;
            message += `--${relatedBoundary}\r\n`;
        }

        // Add main content
        const contentType =
            mailSend.contentType === "HTML" ? "text/html" : "text/plain";
        message += `Content-Type: ${contentType}; charset=UTF-8\r\n`;
        message += `Content-Transfer-Encoding: quoted-printable\r\n\r\n`;
        message += `${this._encodeQuotedPrintable(mailSend.content)}\r\n`;

        // Add inline attachments
        if (hasInlineAttachments) {
            inlineAttachments.forEach((attachment) => {
                message += `\r\n--${relatedBoundary}\r\n`;
                message += `Content-Type: ${attachment.contentType}\r\n`;
                message += `Content-Transfer-Encoding: base64\r\n`;
                message += `Content-ID: <${attachment.contentId}>\r\n`;
                message += `Content-Disposition: inline; filename="${attachment.name}"\r\n\r\n`;
                message += `${attachment.contentBytes}\r\n`;
            });
            message += `--${relatedBoundary}--\r\n`;
        }

        // Add regular attachments
        if (hasRegularAttachments) {
            regularAttachments.forEach((attachment) => {
                message += `\r\n--${mixedBoundary}\r\n`;
                message += `Content-Type: ${attachment.contentType}; name="${attachment.name}"\r\n`;
                message += `Content-Disposition: attachment; filename="${attachment.name}"\r\n`;
                message += `Content-Transfer-Encoding: base64\r\n\r\n`;
                message += `${attachment.contentBytes}\r\n`;
            });
            message += `--${mixedBoundary}--\r\n`;
        }

        logger.info(
            [LOGS.GoogleManager],
            `Message before encoding: ${message}`,
        );

        // Encodage en base64url (Gmail utilise base64url, pas base64 standard)
        const messagesEncoded = encodeBase64URL(message);

        return messagesEncoded;
    }

    /**
     * Extrait une adresse email d'un en-tête
     * gère les formats "Name <email>" et "email"
     */
    private _extractEmailFromHeader(headerValue: string): string | null {
        const emailRegex =
            /<([^>]+)>|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;
        const match = headerValue.match(emailRegex);
        return match ? match[1] || match[2] : null;
    }

    /**
     * Extrait plusieurs adresses email d'un en-tête
     * gère les listes séparées par des virgules
     */
    private _extractEmailsFromHeader(headerValue: string): string[] {
        const emails: string[] = [];
        // Diviser par virgules, mais pas celles dans les guillemets
        const parts = headerValue.split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/);

        parts.forEach((part) => {
            const email = this._extractEmailFromHeader(part.trim());
            if (email) {
                emails.push(email);
            }
        });

        return emails;
    }

    /**
     * Helper to encode content as quoted-printable
     * ═══════════════════════════════════════════════════════════════════════════
     *
     * @param str - String to encode
     * @returns Quoted-printable encoded string
     */
    private _encodeQuotedPrintable(str: string) {
        return str
            .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\xFF]/g, (char) => {
                const hex = char.charCodeAt(0).toString(16).toUpperCase();
                return `=${hex.padStart(2, "0")}`;
            })
            .replace(/(.{1,76})/g, "$1=\r\n")
            .replace(/=\r\n$/, "");
    }

    // #endregion Private Methods
}
